import * as Sentry from "@sentry/nextjs";
import { useEffect, useRef } from "react";

import { identifyUser } from "@/lib/analytics";

/**
 * Minimal interface for the user profile data needed for Mixpanel identification.
 * This avoids coupling to the full ExtendedSelf type from CustomerAccountProvider.
 *
 * Note: We use initials instead of fullName for privacy - we don't want to
 * expose user names to analytics platforms.
 */
interface UserProfileForAnalytics {
  id: string;
  initials: string;
  // fullName is intentionally NOT used for privacy reasons
}

interface UseMixpanelIdentifyParams {
  userProfile: UserProfileForAnalytics | null;
  customerAccountId: string | null;
  isSuccess: boolean;
}

/**
 * Hook to identify the user in Mixpanel when user data becomes available.
 *
 * Uses refs to prevent duplicate identification calls on re-renders.
 * Should be called from CustomerAccountProvider after user profile is loaded.
 *
 * Privacy: Uses initials (e.g., "JS") instead of full name to avoid
 * exposing PII to analytics platforms while still allowing user identification.
 *
 * @example
 * // In CustomerAccountProvider:
 * useMixpanelIdentify({
 *   userProfile: completeUserProfile, // must have { id, initials }
 *   customerAccountId: selectedAccountId,
 *   isSuccess,
 * });
 */
export function useMixpanelIdentify({
  userProfile,
  customerAccountId,
  isSuccess,
}: UseMixpanelIdentifyParams): void {
  const lastUserId = useRef<string | null>(null);
  const lastCustomerAccountId = useRef<string | null>(null);

  useEffect(() => {
    // Only identify when we have user data and the query succeeded
    if (!isSuccess || !userProfile?.id) {
      return;
    }

    // Skip if nothing has changed — prevents unnecessary people.set() calls
    // from React strict mode double-effects and re-renders
    if (
      lastUserId.current === userProfile.id &&
      lastCustomerAccountId.current === customerAccountId
    ) {
      return;
    }

    // Use initials (e.g., "JS") instead of full name for privacy
    identifyUser(userProfile.id, {
      $name: userProfile.initials,
      customerAccountId: customerAccountId ?? undefined,
    });

    Sentry.setUser({
      id: userProfile.id,
      username: userProfile.initials,
    });
    const clientId = process.env.NEXT_PUBLIC_CLIENT_ID;
    if (clientId) Sentry.setTag("whitelabelId", clientId);
    const siloId = process.env.NEXT_PUBLIC_SILO_ID;
    if (siloId) Sentry.setTag("siloId", siloId);

    lastUserId.current = userProfile.id;
    lastCustomerAccountId.current = customerAccountId;
  }, [isSuccess, userProfile?.id, userProfile?.initials, customerAccountId]);
}
